import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { settingsService } from '../../lib/services';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [dpoEmail, setDpoEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await settingsService?.getByCategory('gdpr');
        if (data?.length) {
          data?.forEach(s => {
            if (s?.settingKey === 'privacy_policy_content') setContent(s?.settingValue || '');
            if (s?.settingKey === 'privacy_policy_last_updated') setLastUpdated(s?.settingValue || '');
            if (s?.settingKey === 'gdpr_company_name') setCompanyName(s?.settingValue || '');
            if (s?.settingKey === 'gdpr_dpo_email') setDpoEmail(s?.settingValue || '');
          });
        }
      } catch (e) {
        console.error('Failed to load privacy policy:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const defaultContent = `
    <h2>Privacy Policy</h2>
    <p>This privacy policy explains how we collect, use, and protect your personal data when you visit our website.</p>
    <h3>1. Information We Collect</h3>
    <p>We may collect the following types of information:</p>
    <ul>
      <li>Personal identification information (name, email address, etc.)</li>
      <li>Usage data (pages visited, time spent, browser type)</li>
      <li>Cookie data (see our Cookie Policy below)</li>
    </ul>
    <h3>2. How We Use Your Information</h3>
    <p>We use the information we collect to provide, maintain, and improve our services, communicate with you, and comply with legal obligations.</p>
    <h3>3. Cookies</h3>
    <p>We use cookies to enhance your browsing experience. You can manage your cookie preferences at any time using our cookie consent banner.</p>
    <h3>4. Your Rights</h3>
    <p>Under GDPR, you have the right to access, rectify, erase, restrict processing, and port your personal data. To exercise these rights, please contact us.</p>
    <h3>5. Contact Us</h3>
    <p>If you have questions about this privacy policy, please contact our Data Protection Officer.</p>
  `;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-md border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="ArrowLeft" size={18} />
            <span className="text-sm">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Icon name="Shield" size={18} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">Privacy Policy</span>
          </div>
          <div className="w-16" />
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Icon name="Loader2" size={24} className="text-primary animate-spin mr-2" />
            <span className="text-muted-foreground">Loading privacy policy...</span>
          </div>
        ) : (
          <>
            <div className="mb-8 md:mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="FileText" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground">Privacy Policy</h1>
                  {companyName && <p className="text-sm text-muted-foreground">{companyName}</p>}
                </div>
              </div>
              {lastUpdated && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Calendar" size={14} />
                  Last updated: {new Date(lastUpdated)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              )}
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground
                [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground [&_h2]:mt-8 [&_h2]:mb-4
                [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-foreground [&_h3]:mt-6 [&_h3]:mb-3
                [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p]:mb-4
                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ul]:text-muted-foreground
                [&_li]:text-muted-foreground [&_a]:text-primary [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: content || defaultContent }}
            />

            {dpoEmail && (
              <div className="mt-10 p-5 bg-card border border-border rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Mail" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">Data Protection Officer</h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      For privacy-related enquiries or to exercise your data rights, contact our DPO:
                    </p>
                    <a href={`mailto:${dpoEmail}`} className="text-sm text-primary hover:underline">
                      {dpoEmail}
                    </a>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
